import { Trans, t } from "@lingui/macro";
import { useCallback, useEffect, useState, ReactNode } from "react";
import { FileAttachment } from "../../../models/Message";
import { RoomItem } from "../../../models/RoomItem";
import { UtilApp } from "../../../services/Utils/UtilApp";
import { ImageViewerModal } from "../../components/MessageFileItem";
import { My } from "../../../services/My";
import { Room } from "../../../models/Room";
import { SectionCard, SectionActionButton } from "./SectionCard";

export interface IPanelListImages {
  room: Room,
  items: RoomItem[],
  buttons: ReactNode | null,
}

// 3열 x 2행 = 6개
const MAX_VISIBLE = 6;

export const PanelListImages: React.FC<IPanelListImages> = ({ room, items, buttons }) => {
  const [imageList, setImageList] = useState<{ item: RoomItem, file: FileAttachment }[]>([]);
  const [currentImage, setCurrentImage] = useState(0);
  const [isViewerOpen, setIsViewerOpen] = useState(false);

  useEffect(() => {
    const _list = items.map(i => {
      const f = new FileAttachment(`${i.user.name}, ${UtilApp.dateStrToDay(i.created, My.user.lang)}`, i.url ?? '', 0, 'image', 'image/png');
      return { item: i, file: f };
    });
    setImageList(_list);
  }, [items])

  const openImageViewer = useCallback((index: number) => {
    setCurrentImage(index);
    setIsViewerOpen(true);
  }, []);

  const closeImageViewer = () => {
    setCurrentImage(0);
    setIsViewerOpen(false);
  };

  // 표시할 이미지 (최대 6개)
  const visibleImages = imageList.slice(0, MAX_VISIBLE);
  const remainingCount = imageList.length - MAX_VISIBLE;
  const fileUrls = imageList.map(m => m.file.url);

  if (imageList.length === 0) {
    return null;
  }

  return (
    <SectionCard
      title={t`Images`}
      count={imageList.length}
      collapsible={false}
      actions={buttons}
    >
      {/* 3열 그리드 (2행까지만 표시) */}
      <div className="grid grid-cols-3 gap-2">
        {visibleImages.map((o, index) => (
          <div
            key={o.file.url}
            className="aspect-square rounded-lg overflow-hidden bg-gray-100 border border-gray-200 cursor-pointer hover:opacity-90 transition-opacity"
            onClick={() => openImageViewer(index)}
          >
            <img
              src={o.file.url}
              alt={o.file.filename}
              className="w-full h-full object-cover object-center"
            />
          </div>
        ))}

        {/* 더 있을 경우 마지막 셀에 오버레이로 표시 */}
        {remainingCount > 0 && visibleImages.length === MAX_VISIBLE && (
          <div
            className="aspect-square rounded-lg overflow-hidden bg-gray-900/60 flex items-center justify-center cursor-pointer hover:bg-gray-900/70 transition-colors -mt-[calc(100%+0.5rem)]"
            style={{ gridColumn: 3, gridRow: 2 }}
            onClick={() => openImageViewer(MAX_VISIBLE - 1)}
          >
            <span className="text-white font-semibold text-lg">+{remainingCount}</span>
          </div>
        )}
      </div>

      {/* 이미지 뷰어 모달 */}
      {isViewerOpen && (
        <ImageViewerModal
          images={fileUrls}
          currentImage={currentImage}
          onClose={closeImageViewer}
        />
      )}
    </SectionCard>
  );
}